import { useEffect, useRef, useState } from 'react';
import {
  ArrowLeft,
  ArrowRight,
  Banknote,
  CheckCircle2,
  Loader2,
  MapPin,
  PartyPopper,
  Smartphone,
  User,
  Wallet,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useCart } from '@/contexts/CartContext';
import {
  formatKenyanPhone,
  formatKES,
  isValidKenyanPhone,
  normalizeKenyanPhone,
} from '@/lib/format';
import { getDeliveryEstimate } from '@/lib/time';
import { cn } from '@/lib/utils';

type Step = 'details' | 'payment' | 'processing' | 'success';
type PaymentMethod = 'mpesa' | 'tando' | 'cash';

interface OrderSnapshot {
  id: string;
  total: number;
  count: number;
  method: PaymentMethod;
  phone: string | null;
  area: string;
  placedAt: Date;
}

const PAYMENT_METHODS: Array<{
  id: PaymentMethod;
  name: string;
  tagline: string;
  icon: typeof Smartphone;
  iconClass: string;
}> = [
  {
    id: 'mpesa',
    name: 'M-Pesa',
    tagline: 'STK push to your phone — enter your PIN to pay',
    icon: Smartphone,
    iconClass: 'bg-leaf text-secondary-foreground',
  },
  {
    id: 'tando',
    name: 'Tando',
    tagline: 'Approve the payment request in your Tando app',
    icon: Wallet,
    iconClass: 'bg-marigold text-accent-foreground',
  },
  {
    id: 'cash',
    name: 'Cash on Delivery',
    tagline: 'Pay the rider when your food arrives',
    icon: Banknote,
    iconClass: 'bg-muted text-foreground',
  },
];

const PROCESSING_MESSAGES: Record<Exclude<PaymentMethod, 'cash'>, string[]> = {
  mpesa: [
    'Sending M-Pesa prompt to your phone…',
    'Check your phone and enter your M-Pesa PIN…',
    'Confirming with Safaricom…',
  ],
  tando: [
    'Sending a payment request to Tando…',
    'Approve the request in your Tando app…',
    'Confirming your payment…',
  ],
};

interface CheckoutDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CheckoutDialog({ open, onOpenChange }: CheckoutDialogProps) {
  const { lines, total, count, clear } = useCart();

  const [step, setStep] = useState<Step>('details');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [area, setArea] = useState('');
  const [landmark, setLandmark] = useState('');
  const [notes, setNotes] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [method, setMethod] = useState<PaymentMethod>('mpesa');
  const [paymentPhone, setPaymentPhone] = useState('');
  const [processingMsg, setProcessingMsg] = useState(0);
  const [order, setOrder] = useState<OrderSnapshot | null>(null);

  const timers = useRef<Array<ReturnType<typeof setTimeout>>>([]);

  const clearTimers = () => {
    timers.current.forEach(clearTimeout);
    timers.current = [];
  };

  useEffect(() => clearTimers, []);

  const reset = () => {
    clearTimers();
    setStep('details');
    setErrors({});
    setProcessingMsg(0);
    setOrder(null);
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) reset();
    onOpenChange(next);
  };

  const validateDetails = (): boolean => {
    const next: Record<string, string> = {};
    if (name.trim().length < 2) next.name = 'Please tell us your name';
    if (!isValidKenyanPhone(phone)) next.phone = 'Enter a valid Safaricom/Airtel number e.g. 0712 345 678';
    if (area.trim().length < 3) next.area = 'Which estate or area should we deliver to?';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const goToPayment = () => {
    if (!validateDetails()) return;
    setPaymentPhone(phone);
    setStep('payment');
  };

  const placeOrder = (snapshot: OrderSnapshot) => {
    setOrder(snapshot);
    setStep('success');
    clear();
  };

  const startPayment = () => {
    if (lines.length === 0) return;

    const snapshotBase = {
      id: `MLO-${Math.random().toString(36).slice(2, 8).toUpperCase()}`,
      total,
      count,
      area: [area.trim(), landmark.trim()].filter(Boolean).join(', '),
      placedAt: new Date(),
    };

    if (method === 'cash') {
      placeOrder({ ...snapshotBase, method, phone: null });
      return;
    }

    const normalized = normalizeKenyanPhone(paymentPhone);
    if (!normalized) {
      setErrors({ paymentPhone: 'Enter a valid number e.g. 0712 345 678' });
      return;
    }
    setErrors({});

    setStep('processing');
    setProcessingMsg(0);

    const messages = PROCESSING_MESSAGES[method];
    messages.forEach((_, i) => {
      timers.current.push(setTimeout(() => setProcessingMsg(i), i * 1400));
    });
    timers.current.push(
      setTimeout(() => {
        placeOrder({ ...snapshotBase, method, phone: normalized });
      }, messages.length * 1400 + 800),
    );
  };

  const stepIndex = step === 'details' ? 0 : step === 'payment' ? 1 : 2;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-h-[92vh] w-[calc(100vw-2rem)] max-w-lg overflow-y-auto p-0">
        {/* Step indicator */}
        <div className="border-b border-border bg-muted/40 px-6 pb-4 pt-6">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-black">
              {step === 'details' && 'Delivery details'}
              {step === 'payment' && 'Payment'}
              {step === 'processing' && 'Completing payment'}
              {step === 'success' && 'Order confirmed'}
            </DialogTitle>
            <DialogDescription>
              {step === 'details' && 'Tell us where the rider should find you.'}
              {step === 'payment' && 'Choose how you want to pay. Instant and secure.'}
              {step === 'processing' && 'Almost there — keep your phone close.'}
              {step === 'success' && 'Asante! Your food is being prepared.'}
            </DialogDescription>
          </DialogHeader>

          {step !== 'processing' && (
            <ol className="mt-4 flex items-center gap-2" aria-hidden>
              {['Delivery', 'Payment', 'Done'].map((label, i) => (
                <li key={label} className="flex flex-1 items-center gap-2">
                  <span
                    className={cn(
                      'flex size-6 shrink-0 items-center justify-center rounded-full text-[11px] font-black',
                      i <= stepIndex
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted text-muted-foreground',
                    )}
                  >
                    {i + 1}
                  </span>
                  <span
                    className={cn(
                      'text-xs font-semibold',
                      i <= stepIndex ? 'text-foreground' : 'text-muted-foreground',
                    )}
                  >
                    {label}
                  </span>
                  {i < 2 && <span className="h-px flex-1 bg-border" />}
                </li>
              ))}
            </ol>
          )}
        </div>

        <div className="px-6 py-6">
          {/* ---------------------------------------------------------- Details */}
          {step === 'details' && (
            <form
              className="space-y-4"
              onSubmit={(e) => {
                e.preventDefault();
                goToPayment();
              }}
            >
              <div className="space-y-1.5">
                <Label htmlFor="checkout-name" className="font-semibold">
                  Full name
                </Label>
                <div className="relative">
                  <User className="pointer-events-none absolute left-3.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    id="checkout-name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Amina Wanjiku"
                    className="h-11 pl-10"
                    aria-invalid={Boolean(errors.name)}
                  />
                </div>
                {errors.name && <p className="text-xs font-medium text-destructive">{errors.name}</p>}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="checkout-phone" className="font-semibold">
                  Phone number
                </Label>
                <div className="relative">
                  <Smartphone className="pointer-events-none absolute left-3.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    id="checkout-phone"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="0712 345 678"
                    inputMode="tel"
                    className="h-11 pl-10"
                    aria-invalid={Boolean(errors.phone)}
                  />
                </div>
                {errors.phone && <p className="text-xs font-medium text-destructive">{errors.phone}</p>}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="checkout-area" className="font-semibold">
                  Estate / Area
                </Label>
                <div className="relative">
                  <MapPin className="pointer-events-none absolute left-3.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    id="checkout-area"
                    value={area}
                    onChange={(e) => setArea(e.target.value)}
                    placeholder="Kilimani, Westlands, Ruaka…"
                    className="h-11 pl-10"
                    aria-invalid={Boolean(errors.area)}
                  />
                </div>
                {errors.area && <p className="text-xs font-medium text-destructive">{errors.area}</p>}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="checkout-landmark" className="font-semibold">
                  Building / Landmark <span className="font-normal text-muted-foreground">(optional)</span>
                </Label>
                <Input
                  id="checkout-landmark"
                  value={landmark}
                  onChange={(e) => setLandmark(e.target.value)}
                  placeholder="Green Court, Block B, 3rd floor"
                  className="h-11"
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="checkout-notes" className="font-semibold">
                  Rider notes <span className="font-normal text-muted-foreground">(optional)</span>
                </Label>
                <Textarea
                  id="checkout-notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="e.g. Call when you arrive, extra pili pili please"
                  rows={2}
                />
              </div>

              <Button
                type="submit"
                size="lg"
                className="mt-2 w-full rounded-full text-base font-bold"
              >
                Continue to payment
                <ArrowRight className="size-4" />
              </Button>
            </form>
          )}

          {/* ---------------------------------------------------------- Payment */}
          {step === 'payment' && (
            <form
              className="space-y-4"
              onSubmit={(e) => {
                e.preventDefault();
                startPayment();
              }}
            >
              <div className="space-y-2.5" role="radiogroup" aria-label="Payment method">
                {PAYMENT_METHODS.map(({ id, name: methodName, tagline, icon: Icon, iconClass }) => (
                  <button
                    key={id}
                    type="button"
                    role="radio"
                    aria-checked={method === id}
                    onClick={() => {
                      setMethod(id);
                      setErrors({});
                    }}
                    className={cn(
                      'flex w-full items-center gap-4 rounded-2xl border-2 p-4 text-left transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                      method === id
                        ? 'border-primary bg-primary/5 shadow-sm'
                        : 'border-border bg-card hover:border-primary/40',
                    )}
                  >
                    <span className={cn('flex size-11 shrink-0 items-center justify-center rounded-xl', iconClass)}>
                      <Icon className="size-5" />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block font-bold">{methodName}</span>
                      <span className="block text-xs text-muted-foreground">{tagline}</span>
                    </span>
                    <span
                      className={cn(
                        'flex size-5 shrink-0 items-center justify-center rounded-full border-2',
                        method === id ? 'border-primary bg-primary' : 'border-input',
                      )}
                    >
                      {method === id && <span className="size-2 rounded-full bg-primary-foreground" />}
                    </span>
                  </button>
                ))}
              </div>

              {method !== 'cash' && (
                <div className="space-y-1.5">
                  <Label htmlFor="payment-phone" className="font-semibold">
                    {method === 'mpesa' ? 'M-Pesa' : 'Tando'} phone number
                  </Label>
                  <Input
                    id="payment-phone"
                    value={paymentPhone}
                    onChange={(e) => setPaymentPhone(e.target.value)}
                    placeholder="0712 345 678"
                    inputMode="tel"
                    className="h-11"
                    aria-invalid={Boolean(errors.paymentPhone)}
                  />
                  {errors.paymentPhone ? (
                    <p className="text-xs font-medium text-destructive">{errors.paymentPhone}</p>
                  ) : (
                    <p className="text-xs text-muted-foreground">
                      {method === 'mpesa'
                        ? 'You will receive an STK push — enter your M-Pesa PIN to authorize.'
                        : 'You will receive an approval request in your Tando app.'}
                    </p>
                  )}
                </div>
              )}

              <div className="rounded-xl border border-dashed border-border bg-muted/40 px-4 py-3 text-xs text-muted-foreground">
                <span className="font-bold text-foreground">Demo checkout.</span> This is a preview
                experience — no real payment will be processed.
              </div>

              <div className="flex gap-3 pt-1">
                <Button
                  type="button"
                  size="lg"
                  variant="outline"
                  onClick={() => setStep('details')}
                  className="rounded-full"
                >
                  <ArrowLeft className="size-4" />
                  Back
                </Button>
                <Button
                  type="submit"
                  size="lg"
                  disabled={lines.length === 0}
                  className="flex-1 rounded-full text-base font-bold shadow-lg shadow-primary/25"
                >
                  {method === 'cash' ? `Place order — ${formatKES(total)}` : `Pay ${formatKES(total)}`}
                </Button>
              </div>
            </form>
          )}

          {/* ------------------------------------------------------- Processing */}
          {step === 'processing' && order === null && (
            <div className="flex flex-col items-center py-8 text-center">
              <div className="relative">
                <span className="absolute inset-0 animate-ping-slow rounded-full bg-leaf/40" />
                <span className="relative flex size-20 items-center justify-center rounded-full bg-leaf text-secondary-foreground">
                  {method === 'mpesa' ? <Smartphone className="size-9" /> : <Wallet className="size-9" />}
                </span>
              </div>
              <p className="mt-6 font-display text-xl font-bold">
                {method === 'mpesa' ? 'Check your phone' : 'Open your Tando app'}
              </p>
              <p className="mt-2 flex min-h-6 items-center gap-2 text-sm text-muted-foreground" aria-live="polite">
                <Loader2 className="size-3.5 animate-spin" />
                {PROCESSING_MESSAGES[method as Exclude<PaymentMethod, 'cash'>][processingMsg]}
              </p>
              <p className="mt-4 rounded-full bg-muted px-4 py-1.5 text-xs font-semibold text-muted-foreground">
                Amount: {formatKES(total)} · Do not close this window
              </p>
            </div>
          )}

          {/* ---------------------------------------------------------- Success */}
          {step === 'success' && order && (
            <div className="flex flex-col items-center py-4 text-center">
              <span className="flex size-20 items-center justify-center rounded-full bg-leaf/15 text-leaf dark:text-marigold">
                <PartyPopper className="size-9" />
              </span>
              <p className="mt-5 font-display text-2xl font-black">Asante sana{ name ? `, ${name.split(' ')[0]}` : ''}!</p>
              <p className="mt-1.5 text-sm text-muted-foreground">
                Your order is confirmed and the jiko is already fired up.
              </p>

              <div className="mt-6 w-full space-y-2.5 rounded-2xl border border-border bg-muted/40 p-4 text-left text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Order number</span>
                  <span className="font-black tracking-wide">{order.id}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Items</span>
                  <span className="font-semibold">{order.count}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Deliver to</span>
                  <span className="max-w-[60%] truncate text-right font-semibold">{order.area}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Estimated arrival</span>
                  <span className="font-semibold">{getDeliveryEstimate()} EAT</span>
                </div>
                <div className="flex items-center justify-between border-t border-border pt-2.5">
                  <span className="text-muted-foreground">
                    {order.method === 'cash'
                      ? 'To pay on delivery'
                      : `Paid via ${order.method === 'mpesa' ? 'M-Pesa' : 'Tando'}`}
                  </span>
                  <span className="flex items-center gap-1.5 font-black text-primary">
                    {order.method !== 'cash' && <CheckCircle2 className="size-4 text-leaf dark:text-marigold" />}
                    {formatKES(order.total)}
                  </span>
                </div>
                {order.phone && (
                  <p className="text-xs text-muted-foreground">
                    Receipt sent to {formatKenyanPhone(order.phone)}
                  </p>
                )}
              </div>

              <Button
                size="lg"
                onClick={() => handleOpenChange(false)}
                className="mt-6 w-full rounded-full text-base font-bold"
              >
                Done — back to the menu
              </Button>
              <p className="mt-3 text-xs text-muted-foreground">
                Demo checkout — no real payment was processed.
              </p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
